import GavelIcon from '@mui/icons-material/Gavel';
import StarIcon from '@mui/icons-material/Star';
import VerifiedIcon from '@mui/icons-material/Verified';
import {
    Box,
    Chip,
    Container,
    Fab,
    Link,
    Paper,
    Typography
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Footer from './Footer';
import LawAppBar from './LawAppBar';
import './Pricing.css';

const planDetailLineSx = {
    color: 'var(--text-primary)',
    fontSize: '0.875rem',
    lineHeight: 1.65,
    marginBottom: '8px',
    '&:last-child': {
        marginBottom: 0
    }
};

const planDetailsBoxSx = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginBottom: '16px'
};

const advantageItems = [
    {
        title: 'Built for Precision',
        description:
            'Our AccuAide™ Integrity Engine proactively finds and flags issues for your professional review. All information is linked to the original page, allowing for one-click human verification.'
    },
    {
        title: 'Universal document support',
        description:
            'Drop in medical reports, bills, police reports, deposition transcripts, interrogatories, client intake forms, and more.'
    },
    {
        title: 'Consolidation',
        description:
            'De-duped chronologies and per patient medical reports with list of pre-existing conditions, diagnoses, and treatments.'
    },
    {
        title: 'Unlimited drafts & exports',
        description:
            'Refine and export your Demand Letter text and Bills of Particulars as many times as you need.'
    }
];

function AdvantageItem({ title, description }) {
    return (
        <Box sx={{ marginBottom: '24px', '&:last-child': { marginBottom: 0 } }}>
            <Typography
                variant="h6"
                component="h3"
                sx={{
                    color: 'var(--text-primary)',
                    fontWeight: 500,
                    fontSize: '1.1rem',
                    marginBottom: '4px',
                    lineHeight: 1.3
                }}
            >
                {title}
            </Typography>
            <Typography
                variant="body1"
                sx={{
                    color: 'var(--text-primary)',
                    fontSize: { xs: '1rem', sm: '1.05rem' },
                    lineHeight: 1.8
                }}
            >
                {description}
            </Typography>
        </Box>
    );
}

export default function Pricing() {
    const navigate = useNavigate();

    return (
        <div className="App">
            <LawAppBar />
            <Container
                maxWidth="lg"
                sx={{
                    padding: { xs: '20px', sm: '40px' },
                    marginTop: '40px',
                    marginBottom: '60px'
                }}
            >
                {/* Hero Section */}
                <Box sx={{ textAlign: 'center', marginBottom: '60px' }}>
                    <Typography
                        variant="h3"
                        component="h1"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 400,
                            fontSize: { xs: '2rem', sm: '2.8rem', md: '3.2rem' },
                            marginBottom: '1rem',
                            letterSpacing: '-0.02em',
                            lineHeight: 1.2
                        }}
                    >
                        Transparent Pricing. No Contracts. No Friction.
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{
                            color: 'var(--text-secondary)',
                            fontSize: { xs: '1rem', sm: '1.2rem' },
                            fontWeight: 400,
                            maxWidth: 800,
                            margin: '0 auto',
                            lineHeight: 1.6
                        }}
                    >
                        Stop losing hours to manual medical record reviews. Win more cases.
                    </Typography>
                </Box>

                {/* Select Your Plan intro */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px',
                        textAlign: 'center'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '16px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        Select Your Plan
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-secondary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            maxWidth: 720,
                            margin: '0 auto'
                        }}
                    >
                        Choose the capacity that fits your firm's monthly case volume. All plans are month-to-month.
                    </Typography>
                </Paper>

                {/* Plan Cards */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                        gap: 3,
                        alignItems: 'stretch',
                        textAlign: 'left',
                        marginBottom: '48px'
                    }}
                >
                    {/* Basic */}
                    <Paper
                        elevation={0}
                        sx={{
                            padding: { xs: '28px', sm: '32px' },
                            borderRadius: '8px',
                            boxShadow: 'var(--shadow-light)',
                            border: '1px solid var(--border-color)',
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%'
                        }}
                    >
                        <Typography
                            variant="h5"
                            component="h3"
                            sx={{
                                color: 'var(--text-primary)',
                                fontWeight: 600,
                                fontSize: '1.35rem',
                                marginBottom: '12px'
                            }}
                        >
                            Basic
                        </Typography>
                        <Typography
                            variant="h4"
                            sx={{
                                color: 'var(--text-primary)',
                                fontWeight: 500,
                                marginBottom: '16px'
                            }}
                        >
                            $95
                            <Typography component="span" variant="body2" sx={{ color: 'var(--text-secondary)', fontWeight: 400 }}>
                                {' '}
                                per month
                            </Typography>
                        </Typography>
                        <Box sx={planDetailsBoxSx}>
                            <Typography variant="body2" sx={{ ...planDetailLineSx, fontWeight: 500 }}>
                                1,000 pages uploaded included, 10¢/page overage.
                            </Typography>
                            <Typography variant="body2" sx={planDetailLineSx}>
                                Includes the full feature set.
                            </Typography>
                        </Box>
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'var(--text-secondary)',
                                fontStyle: 'italic',
                                marginTop: 'auto',
                                paddingTop: '16px',
                                borderTop: '1px solid var(--border-color)'
                            }}
                        >
                            Best for occasional use
                        </Typography>
                    </Paper>

                    {/* Professional - Most Popular */}
                    <Paper
                        elevation={0}
                        sx={{
                            padding: { xs: '28px', sm: '32px' },
                            borderRadius: '8px',
                            boxShadow: 'var(--shadow-light)',
                            border: '2px solid var(--accent-color)',
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            position: 'relative'
                        }}
                    >
                        <Chip
                            icon={<StarIcon sx={{ fontSize: '1rem !important' }} />}
                            label="Most popular plan"
                            size="small"
                            sx={{
                                position: 'absolute',
                                top: -10,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                background: 'var(--button-gradient)',
                                color: '#1a1a1a',
                                fontWeight: 600,
                                '& .MuiChip-icon': { color: '#1a1a1a' }
                            }}
                        />
                        <Typography
                            variant="h5"
                            component="h3"
                            sx={{
                                color: 'var(--text-primary)',
                                fontWeight: 600,
                                fontSize: '1.35rem',
                                marginBottom: '12px',
                                marginTop: '8px'
                            }}
                        >
                            Professional
                        </Typography>
                        <Typography
                            variant="h4"
                            sx={{
                                color: 'var(--text-primary)',
                                fontWeight: 500,
                                marginBottom: '16px'
                            }}
                        >
                            $195
                            <Typography component="span" variant="body2" sx={{ color: 'var(--text-secondary)', fontWeight: 400 }}>
                                {' '}
                                per month
                            </Typography>
                        </Typography>
                        <Box sx={planDetailsBoxSx}>
                            <Typography variant="body2" sx={{ ...planDetailLineSx, fontWeight: 500 }}>
                                3,000 pages uploaded included, 5¢/page overage.
                            </Typography>
                            <Typography variant="body2" sx={planDetailLineSx}>
                                Higher capacity designed to cover the monthly volume of an active practice.
                            </Typography>
                        </Box>
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'var(--text-secondary)',
                                fontStyle: 'italic',
                                marginTop: 'auto',
                                paddingTop: '16px',
                                borderTop: '1px solid var(--border-color)'
                            }}
                        >
                            Best for active solo attorneys, small, medium PI firms, legal nurse consultants, and medical reviewers
                        </Typography>
                    </Paper>

                    {/* Enterprise */}
                    <Paper
                        elevation={0}
                        sx={{
                            padding: { xs: '28px', sm: '32px' },
                            borderRadius: '8px',
                            boxShadow: 'var(--shadow-light)',
                            border: '1px solid var(--border-color)',
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%'
                        }}
                    >
                        <Typography
                            variant="h5"
                            component="h3"
                            sx={{
                                color: 'var(--text-primary)',
                                fontWeight: 600,
                                fontSize: '1.35rem',
                                marginBottom: '12px'
                            }}
                        >
                            Enterprise
                        </Typography>
                        <Typography
                            variant="h4"
                            sx={{
                                color: 'var(--text-primary)',
                                fontWeight: 500,
                                marginBottom: '16px',
                                fontSize: { xs: '1.5rem', sm: '1.75rem' },
                                lineHeight: 1.3
                            }}
                        >
                            Custom pricing
                        </Typography>
                        <Box sx={planDetailsBoxSx}>
                            <Typography variant="body2" sx={planDetailLineSx}>
                                Custom integration, workflows, and APIs.
                            </Typography>
                        </Box>
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'var(--text-secondary)',
                                fontStyle: 'italic',
                                marginTop: 'auto',
                                paddingTop: '16px',
                                borderTop: '1px solid var(--border-color)'
                            }}
                        >
                            Best for large firms and highly specialized workflows
                        </Typography>
                    </Paper>
                </Box>

                {/* The AttorneyAide Advantage */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px',
                        textAlign: 'left'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                        <VerifiedIcon
                            sx={{
                                fontSize: { xs: '2rem', sm: '2.5rem' },
                                color: 'var(--accent-color)'
                            }}
                        />
                        <Typography
                            variant="h4"
                            component="h2"
                            sx={{
                                color: 'var(--text-primary)',
                                fontWeight: 500,
                                fontSize: { xs: '1.8rem', sm: '2.2rem' },
                                letterSpacing: '-0.01em',
                                margin: 0
                            }}
                        >
                            The AttorneyAide Advantage
                        </Typography>
                    </Box>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-secondary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '32px'
                        }}
                    >
                        Every plan includes the full feature set
                    </Typography>
                    {advantageItems.map((item) => (
                        <AdvantageItem key={item.title} title={item.title} description={item.description} />
                    ))}
                </Paper>

                {/* FAQs */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px',
                        textAlign: 'left'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        FAQs
                    </Typography>

                    <Box sx={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '20px', marginBottom: '20px' }}>
                        <Typography sx={{ color: 'var(--text-primary)', fontWeight: 500, marginBottom: '8px' }} component="h3" variant="subtitle1">
                            How does page usage work?
                        </Typography>
                        <Typography sx={{ color: 'var(--text-primary)', lineHeight: 1.8 }}>
                            Each plan includes a monthly page allowance for document processing. When you exceed your included pages, overage is billed at your plan rate.
                        </Typography>
                    </Box>
                    <Box sx={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '20px', marginBottom: '20px' }}>
                        <Typography sx={{ color: 'var(--text-primary)', fontWeight: 500, marginBottom: '8px' }} component="h3" variant="subtitle1">
                            What happens if I go over the included monthly page allowance?
                        </Typography>
                        <Typography sx={{ color: 'var(--text-primary)', lineHeight: 1.8 }}>
                            Nothing stops. We never lock you out or interrupt a live document upload. If you process a heavy file that pushes you over your limit, additional pages are seamlessly billed at the plan's overage rate.
                        </Typography>
                    </Box>
                    <Box sx={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '20px', marginBottom: '20px' }}>
                        <Typography sx={{ color: 'var(--text-primary)', fontWeight: 500, marginBottom: '8px' }} component="h3" variant="subtitle1">
                            Can I upgrade later? What happens to my money?
                        </Typography>
                        <Typography sx={{ color: 'var(--text-primary)', lineHeight: 1.8 }}>
                            You can change your plan at any time. All charges are prorated.
                        </Typography>
                    </Box>
                    <Box sx={{ borderBottom: '1px solid var(--border-color)', paddingBottom: '20px', marginBottom: '20px' }}>
                        <Typography sx={{ color: 'var(--text-primary)', fontWeight: 500, marginBottom: '8px' }} component="h3" variant="subtitle1">
                            Do I have to sign a long-term contract?
                        </Typography>
                        <Typography sx={{ color: 'var(--text-primary)', lineHeight: 1.8 }}>
                            No. All plans are billed monthly. You can cancel or change your plan at any time. All charges are prorated. We believe our value should keep you here, not a legal contract.
                        </Typography>
                    </Box>
                    <Box sx={{ paddingBottom: '0', marginBottom: '0' }}>
                        <Typography sx={{ color: 'var(--text-primary)', fontWeight: 500, marginBottom: '8px' }} component="h3" variant="subtitle1">
                            What if I have massive case loads with 100K pages of medical records?
                        </Typography>
                        <Typography sx={{ color: 'var(--text-primary)', lineHeight: 1.8 }}>
                            Our platform is designed to deal with large files. We can set up custom optimizations for high-volume litigation without affecting throughput or latency. If you process large workloads, then <Link
                                href="/contact"
                                sx={{
                                    color: 'var(--text-primary)',
                                    fontWeight: 600,
                                    textDecoration: 'underline',
                                    '&:hover': { color: 'var(--text-secondary)', textDecoration: 'underline', opacity: 0.9 }
                                }}
                            >
                                let us know
                            </Link> so that we can provide the best experience.
                        </Typography>
                    </Box>
                </Paper>

                {/* Need Help CTA */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        textAlign: 'center'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '16px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        Need Help Choosing a Plan?
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-secondary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '24px'
                        }}
                    >
                        If you're unsure which plan fits your workflow or have questions, we're happy to help.
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
                        <Link
                            href="/contact"
                            sx={{
                                color: 'var(--text-primary)',
                                fontWeight: 600,
                                textDecoration: 'underline',
                                '&:hover': { color: 'var(--text-secondary)', textDecoration: 'underline', opacity: 0.9 }
                            }}
                        >
                            Contact Us
                        </Link>
                        <Typography component="span" sx={{ color: 'var(--text-secondary)' }}>
                            or
                        </Typography>
                        <Link
                            href="https://calendar.app.google/LuoqX3Xk43KMWquv5"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                color: 'var(--text-primary)',
                                fontWeight: 600,
                                textDecoration: 'underline',
                                '&:hover': { color: 'var(--text-secondary)', textDecoration: 'underline', opacity: 0.9 }
                            }}
                        >
                            Schedule a quick 15-minute call
                        </Link>
                    </Box>
                </Paper>

                {/* Call to Action */}
                <Box
                    sx={{
                        textAlign: 'center',
                        padding: { xs: '40px 20px', sm: '60px 40px' },
                        marginTop: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 400,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '32px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        Ready to reclaim your time?
                    </Typography>
                    <Fab
                        variant="extended"
                        size="large"
                        color="primary"
                        onClick={() => navigate('/mycases')}
                        sx={{
                            background: 'var(--button-gradient)',
                            boxShadow: '0 6px 20px rgba(212, 175, 55, 0.4)',
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '1.05rem',
                            padding: '15px 28px',
                            borderRadius: '16px',
                            minHeight: '56px',
                            height: '56px',
                            lineHeight: '1.5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: 'auto',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            overflow: 'hidden',
                            border: 'none',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 12px 30px rgba(212, 175, 55, 0.6)',
                                background: 'var(--button-gradient-hover)'
                            },
                            '&:active': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 6px 20px rgba(212, 175, 55, 0.4)'
                            },
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: '-100%',
                                width: '100%',
                                height: '100%',
                                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                                transition: 'left 0.6s'
                            },
                            '&:hover::before': {
                                left: '100%'
                            }
                        }}
                    >
                        <GavelIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
                        Start Your Free Trial Now
                    </Fab>
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'var(--text-secondary)',
                            marginTop: '16px',
                            fontSize: '0.95rem'
                        }}
                    >
                        HIPAA-grade security • No contracts • No setup • No training required
                    </Typography>
                </Box>
            </Container>
            <Footer />
        </div>
    );
}
